'use client';

import { useState, useEffect } from 'react';
import { useCounter } from '@/components/marketing/engine/use-counter';

/* ───────────── AI REPORTS — structured assessment report assembles ─────────────
   Score strip + ability progression (theta) + recommendation.
   Grounded in the live Assessment Report. */

interface Cell {
  v: string;
  k: string;
  accent?: boolean;
}

export function ReportsExp() {
  const [step, setStep] = useState<number>(0);

  useEffect(() => {
    const t = [350, 1000, 1700, 2500, 3200].map((ms, i) =>
      setTimeout(() => setStep(i + 1), ms)
    );
    return () => t.forEach(clearTimeout);
  }, []);

  const overall = useCounter(87, 1300, step >= 1, 0);

  const CELLS: Cell[] = [
    { v: `${step >= 1 ? overall : 0}%`, k: 'Overall' },
    { v: '26/30', k: 'Correct' },
    { v: '17m', k: 'Duration' },
    { v: 'Top 5%', k: 'Percentile' },
    { v: 'High', k: 'Trust', accent: true },
  ];

  const STRENGTHS = ['Distributed systems', 'API design', 'Concurrency'];

  // theta progression — normalized 0..1 ability across 12 questions
  const THETA = [0.32, 0.4, 0.5, 0.46, 0.58, 0.66, 0.6, 0.7, 0.78, 0.74, 0.84, 0.9];

  return (
    <div style={{ height: '100%', background: 'var(--surface)', borderRadius: 'var(--r-md)', border: '1px solid var(--border)', padding: '16px 18px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '30px', height: '30px', borderRadius: '9px', background: 'var(--green-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '11px', color: 'var(--green)' }}>PS</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600 }}>Assessment report</div>
            <span className="mono" style={{ fontSize: '9px', color: 'var(--text-3)' }}>Sr. Backend · PS-4471</span>
          </div>
        </div>
        <span className="mono" style={{ fontSize: '9px', color: step >= 5 ? 'var(--green)' : 'var(--text-3)', transition: 'color .4s' }}>{step >= 5 ? '● COMPLETE' : 'assembling…'}</span>
      </div>

      {/* score strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px', marginBottom: '14px' }}>
        {CELLS.map((c, i) => (
          <div key={i} style={{ padding: '9px 6px', borderRadius: 'var(--r-sm)', textAlign: 'center',
            background: c.accent ? 'var(--green-soft)' : 'var(--surface-raised)',
            border: `1px solid ${c.accent ? 'color-mix(in srgb, var(--green) 22%, transparent)' : 'var(--border)'}`,
            opacity: step >= 1 ? 1 : 0, transform: step >= 1 ? 'none' : 'translateY(6px)', transition: `all .5s var(--ease) ${i * 0.08}s` }}>
            <div className="mono" style={{ fontSize: '14px', fontWeight: 700, color: c.accent ? 'var(--green)' : 'var(--text)', lineHeight: 1.1 }}>{c.v}</div>
            <div style={{ fontSize: '8px', color: 'var(--text-3)', marginTop: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{c.k}</div>
          </div>
        ))}
      </div>

      {/* ability progression (theta) */}
      <div className="label" style={{ fontSize: '8px', marginBottom: '6px', opacity: step >= 2 ? 1 : 0.2, transition: 'opacity .4s' }}>ABILITY PROGRESSION · θ</div>
      <div style={{ position: 'relative', height: '54px', marginBottom: '14px', borderRadius: 'var(--r-sm)', background: 'var(--surface-raised)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <ThetaChart points={THETA} active={step >= 2} />
      </div>

      {/* verified strengths */}
      <div className="label" style={{ fontSize: '8px', marginBottom: '7px', opacity: step >= 3 ? 1 : 0.2, transition: 'opacity .4s' }}>VERIFIED STRENGTHS</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
        {STRENGTHS.map((s, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: 'var(--r-full)',
            background: 'var(--green-soft)', border: '1px solid color-mix(in srgb, var(--green) 22%, transparent)', fontSize: '10.5px', fontWeight: 500, color: 'var(--green)',
            opacity: step >= 3 ? 1 : 0, transform: step >= 3 ? 'none' : 'scale(0.9)', transition: `all .4s var(--ease) ${i * 0.1}s` }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>{s}
          </span>
        ))}
      </div>

      {/* recommendation */}
      <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 15px', borderRadius: 'var(--r-md)',
        background: step >= 4 ? 'var(--green-soft)' : 'var(--surface-raised)', border: `1px solid ${step >= 4 ? 'color-mix(in srgb, var(--green) 30%, transparent)' : 'var(--border)'}`,
        opacity: step >= 4 ? 1 : 0.25, transition: 'all .6s var(--ease)' }}>
        <div>
          <div style={{ fontSize: '12.5px', fontWeight: 600 }}>Recommendation</div>
          <span className="mono" style={{ fontSize: '8.5px', color: 'var(--text-3)' }}>confidence 92% · trust high</span>
        </div>
        <span style={{ padding: '4px 12px', borderRadius: '6px', background: 'var(--green)', color: '#04130d', fontSize: '10px', fontWeight: 700, fontFamily: 'var(--mono)', letterSpacing: '0.05em' }}>ADVANCE</span>
      </div>
    </div>
  );
}

/* theta line — draws itself in, then settles */
function ThetaChart({ points, active }: { points: number[]; active: boolean }) {
  const W = 100, H = 100;
  const coords = points.map((p, i) => {
    const x = (i / (points.length - 1)) * W;
    const y = H - p * (H - 12) - 6;
    return [x, y];
  });
  const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const area = `${line} L${W} ${H} L0 ${H} Z`;
  const [endX, endY] = coords[coords.length - 1];

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ display: 'block' }}>
        <defs>
          <linearGradient id="theta-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--indigo-light)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--indigo-light)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#theta-fill)" style={{ opacity: active ? 1 : 0, transition: 'opacity .8s ease .4s' }} />
        <path d={line} fill="none" stroke="var(--indigo-light)" strokeWidth="1.4" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round"
          style={{ strokeDasharray: 300, strokeDashoffset: active ? 0 : 300, transition: 'stroke-dashoffset 1.3s var(--ease)' }} />
      </svg>
      {/* non-distorting endpoint dot */}
      <span style={{ position: 'absolute', left: `${endX}%`, top: `${endY}%`, width: '7px', height: '7px', borderRadius: '50%',
        background: 'var(--green)', boxShadow: '0 0 8px 1px color-mix(in srgb, var(--green) 60%, transparent)', transform: 'translate(-50%, -50%)',
        opacity: active ? 1 : 0, transition: 'opacity .4s ease 1.2s' }}></span>
    </div>
  );
}
